import { useState, useCallback } from 'react';

function readStored<T>(key: string, fallback: T): T {
  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * useState wrapper that persists the value to localStorage.
 * Handles JSON serialization and SSR safely.
 *
 * The key is tracked alongside the value so that a caller which swaps keys
 * while mounted (e.g. a list page scoping its filters per `?view=`) re-reads
 * the new key. Otherwise the previous key's value carried over and the next
 * write persisted it under the new key, cross-contaminating the two.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const [state, setState] = useState<{ key: string; value: T }>(() => ({
    key,
    value: readStored(key, initialValue),
  }));

  // Render-phase reset — React's recommended alternative to an effect for
  // state derived from props, and avoids rendering one frame of stale data.
  if (state.key !== key) {
    setState({ key, value: readStored(key, initialValue) });
  }

  const value = state.key === key ? state.value : readStored(key, initialValue);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev.value) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Storage may be full or disabled — fail silently
        }
        return { key, value: resolved };
      });
    },
    [key],
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setState({ key, value: initialValue });
  }, [key, initialValue]);

  return [value, setValue, removeValue];
}
